import * as THREE from 'three';
import { CELL } from '../core/config';
import { cellMass, type Vec } from '../destruction/structure';
import { MATERIALS, type Cell, type CellBounds } from '../world/city';

/** Exact mass/COM of our occupied volume model, independent of collider merging. */
export function aggregateMass(cells: Iterable<Cell>, origin: Vec) {
  const work = aggregateMassSteps(cells, origin);
  let result = work.next();
  while (!result.done) result = work.next();
  return result.value;
}

/** Accumulate first and second moments in one pass about the nearby body origin.
 * The parallel-axis shift retains the full inertia tensor without allocating
 * per-cell vectors, cloned multipart cells or a second full-cell list. */
export function* aggregateMassSteps(cells: Iterable<Cell>, origin: Vec) {
  let mass = 0,
    mx = 0,
    my = 0,
    mz = 0,
    count = 0;
  const a = [
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0],
  ];
  const add = (c: Cell, b: CellBounds | undefined, m: number) => {
    const x = (c.x + (b ? b.x + b.w / 2 : 0.5)) * CELL - origin.x,
      y = (c.y + (b ? b.y + b.h / 2 : 0.5)) * CELL - origin.y,
      z = (c.z + (b ? b.z + b.d / 2 : 0.5)) * CELL - origin.z;
    const w = CELL * (b?.w ?? 1),
      h = CELL * (b?.h ?? 1),
      d = CELL * (b?.d ?? 1);
    mass += m;
    mx += m * x;
    my += m * y;
    mz += m * z;
    a[0][0] += m * ((h * h + d * d) / 12 + y * y + z * z);
    a[1][1] += m * ((w * w + d * d) / 12 + x * x + z * z);
    a[2][2] += m * ((w * w + h * h) / 12 + x * x + y * y);
    a[0][1] -= m * x * y;
    a[0][2] -= m * x * z;
    a[1][2] -= m * y * z;
  };
  for (const c of cells) {
    if (c.parts)
      for (const part of c.parts)
        add(c, part, MATERIALS[c.material].density * CELL ** 3 * part.w * part.h * part.d * 25);
    else add(c, c.bounds, cellMass(c) * 25);
    if (++count % 1024 === 0) yield;
  }
  const denominator = Math.max(0.001, mass),
    x = mx / denominator,
    y = my / denominator,
    z = mz / denominator;
  const center = mass > 0 ? new THREE.Vector3(x, y, z) : new THREE.Vector3(-origin.x, -origin.y, -origin.z);
  a[0][0] -= mass * (y * y + z * z);
  a[1][1] -= mass * (x * x + z * z);
  a[2][2] -= mass * (x * x + y * y);
  a[0][1] += mass * x * y;
  a[0][2] += mass * x * z;
  a[1][2] += mass * y * z;
  a[1][0] = a[0][1];
  a[2][0] = a[0][2];
  a[2][1] = a[1][2];
  const v = [
    [1, 0, 0],
    [0, 1, 0],
    [0, 0, 1],
  ];
  // Jacobi diagonalization of the symmetric inertia tensor: no cell colliders
  // or runtime constraints are required to retain off-axis mass distribution.
  for (let iteration = 0; iteration < 18; iteration++) {
    let p = 0,
      q = 1;
    for (const [i, j] of [
      [0, 2],
      [1, 2],
    ])
      if (Math.abs(a[i][j]) > Math.abs(a[p][q])) {
        p = i;
        q = j;
      }
    if (Math.abs(a[p][q]) < 1e-7) break;
    const theta = 0.5 * Math.atan2(2 * a[p][q], a[q][q] - a[p][p]),
      c = Math.cos(theta),
      s = Math.sin(theta),
      app = a[p][p],
      aqq = a[q][q],
      apq = a[p][q];
    for (let k = 0; k < 3; k++)
      if (k !== p && k !== q) {
        const kp = a[k][p],
          kq = a[k][q];
        a[k][p] = a[p][k] = c * kp - s * kq;
        a[k][q] = a[q][k] = s * kp + c * kq;
      }
    a[p][p] = c * c * app - 2 * s * c * apq + s * s * aqq;
    a[q][q] = s * s * app + 2 * s * c * apq + c * c * aqq;
    a[p][q] = a[q][p] = 0;
    for (let k = 0; k < 3; k++) {
      const kp = v[k][p],
        kq = v[k][q];
      v[k][p] = c * kp - s * kq;
      v[k][q] = s * kp + c * kq;
    }
  }
  const matrix = new THREE.Matrix4().set(
    v[0][0],
    v[0][1],
    v[0][2],
    0,
    v[1][0],
    v[1][1],
    v[1][2],
    0,
    v[2][0],
    v[2][1],
    v[2][2],
    0,
    0,
    0,
    0,
    1,
  );
  const frame = new THREE.Quaternion().setFromRotationMatrix(matrix).normalize();
  return {
    mass,
    center,
    inertia: { x: Math.max(0.001, a[0][0]), y: Math.max(0.001, a[1][1]), z: Math.max(0.001, a[2][2]) },
    frame,
  };
}
